import React, { useState, useMemo } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableCellAlign,
  TableBody,
  TableCell,
  TableSortDirection,
  Announce,
  IconButton,
  VisuallyHidden,
  ButtonVariant,
  ButtonType
} from 'react-magma-dom';
import {
  ExpandLessIcon,
  ExpandMoreIcon
} from 'react-magma-icons';
import PropTypes from 'prop-types';
import { getSorter } from './helper';

const remove = (array, element) => {
  const index = array.indexOf(element);

  if (index !== -1) {
    array.splice(index, 1);
  }
};

const HiddenColumns = props => {
  const { item, cols } = props;
  const UI = cols.map(col => {
    const colValue = item[col];
    return (
      <p key={col}>
        <label>{col}</label> <span>{colValue}</span>
      </p>
    );
  });

  return <div>{UI}</div>;
};

HiddenColumns.propTypes = {
  item: PropTypes.object.isRequired,
  cols: PropTypes.array.isRequired
};

const AccordionTable = props => {
  const {
    rows,
    order,
    style,
    testId,
    keyColumn,
    initialColumn,
    columnConfigs,
    hiddenColumns,
    className = '',
    messageForEmptyTable = '',
    HiddenColumnsComponent = null
  } = props;
  const [sortConfig, setSortConfig] = useState({
    key: initialColumn,
    direction: TableSortDirection.ascending,
    message: ''
  });

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const sortedItems = useMemo(() => {
    if (!rows || rows.length === 0) {
      return [];
    }
    const sortableItems = [...rows];
    if (sortConfig !== null) {
      sortableItems.sort(getSorter(sortConfig));
    }
    return sortableItems;
  }, [rows, sortConfig]);

  const headers = useMemo(() => {
    return order ? order : Object.keys(rows[0]);
  }, [rows, order]);

  const requestSort = key => {
    let direction = TableSortDirection.ascending;
    if (
      sortConfig &&
			sortConfig.key === key &&
			sortConfig.direction === TableSortDirection.ascending
    ) {
      direction = TableSortDirection.descending;
    }
    const message = `Table is sorted by ${key}, ${direction}`;
    setSortConfig({ key, direction, message });
  };

  const toggleHiddenColumns = (rowKey, isExpanded) => {
    const updatedKeys = expandedRowKeys.slice();
    if (isExpanded) {
      remove(updatedKeys, rowKey);
    } else {
      updatedKeys.push(rowKey);
    }
    setExpandedRowKeys(updatedKeys);
  };

  const headerCellsUI = headers.map(header => {
    const config = columnConfigs ? columnConfigs[header] : null;
    const textUI = (config && config.alias) ? config.alias : header.charAt(0).toUpperCase() + header.slice(1);
    const styles = header === 'variables' ? { minWidth: '135px' } : {};

    return (
      <TableHeaderCell
        key={header}
        align={config && config.align}
        onSort={() => {
          requestSort(header);
        }}
        style={styles}
        isSortable={config && config.isSortable}
        sortDirection={
          sortConfig.key === header
            ? sortConfig.direction
            : TableSortDirection.none
        }
      >
        {textUI}
      </TableHeaderCell>
    );
  });

  if (hiddenColumns) {
    headerCellsUI.push(<TableHeaderCell key="details">Details</TableHeaderCell>);
  }

  let rowsUI = [];
  if (sortedItems.length === 0) {
    rowsUI =
      <TableRow >
        <TableCell align={TableCellAlign.center} colSpan={headers.length + 1}>
          {messageForEmptyTable}
        </TableCell>
      </TableRow>;
  }
  for (let rowIndex = 0; rowIndex < sortedItems.length; rowIndex++) {
    const item = sortedItems[rowIndex];
    const keyColValue = item[keyColumn];
    let showHiddenColumns = false;

    const cellsUI = headers.map(columnName => {
      const itemValue = item[columnName];
      const config = columnConfigs ? columnConfigs[columnName] : null;
      if (config) {
        const { align, formatter } = config;
        const cellUI = formatter
          ? formatter(itemValue, item)
          : itemValue;
        return <TableCell key={columnName} align={align}>{cellUI}</TableCell>;
      }
      const createMarkup = html =>
        ({ __html: html });

      return <TableCell key={columnName} dangerouslySetInnerHTML={createMarkup(itemValue)} />;
    });
    if (hiddenColumns) {
      const indexInExpandedRowsList = expandedRowKeys.indexOf(keyColValue);
      showHiddenColumns = indexInExpandedRowsList >= 0;
      cellsUI.push(
        <TableCell key="more-info">
          <IconButton
            aria-expanded={showHiddenColumns}
            variant={ButtonVariant.link}
            icon={showHiddenColumns ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            type={ButtonType.button}
            onClick={() => toggleHiddenColumns(keyColValue, showHiddenColumns)}
            testId="button-hidden-columns"
          />
        </TableCell>
      );
    }

    rowsUI.push(<TableRow key={keyColValue}>{cellsUI}</TableRow>);
    if (showHiddenColumns) {
      const HiddenUIComponent = HiddenColumnsComponent ? HiddenColumnsComponent : HiddenColumns;
      rowsUI.push((
        <TableRow key={`${keyColValue}-expanded`}>
          <TableCell aria-colspan={`${headers.length + 1}`} colSpan={`${headers.length + 1}`}>
            <HiddenUIComponent cols={hiddenColumns} item={item} />
          </TableCell>
        </TableRow>
      ));
    }
  }

  return (
    <>
      <Table hasVerticalBorders style={style} className={className} testId={testId}>
        <TableHead>
          <TableRow>{headerCellsUI}</TableRow>
        </TableHead>
        <TableBody>{rowsUI}</TableBody>
      </Table>
      <Announce>
        <VisuallyHidden>{sortConfig.message}</VisuallyHidden>
      </Announce>
    </>
  );
};

AccordionTable.propTypes = {
  testId: PropTypes.string,
  initialColumn: PropTypes.string,
  keyColumn: PropTypes.string.isRequired,
  rows: PropTypes.array,
  columnConfigs: PropTypes.object,
  order: PropTypes.array,
  hiddenColumns: PropTypes.array,
  style: PropTypes.object,
  className: PropTypes.string,
  messageForEmptyTable: PropTypes.string,
  HiddenColumnsComponent: PropTypes.elementType
};

export default AccordionTable;
